import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { IonInput, ToastController } from '@ionic/angular';
import { BehaviorSubject, Subject } from 'rxjs';
import { IAffiliates, IUser } from '../auth.models';
import { countries } from './countries';
import { FormService } from 'src/app/shared/components/form/form.service';
import { MapService } from 'src/app/services/map.service';
import { AuthService } from '../auth.service';

export interface ICountry {
  name: string;
  code: string;
}

export function matchValidator(
  matchTo: string,
  reverse?: boolean
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const { parent } = control;
    if (parent && reverse) {
      const c = (parent?.controls as any)[matchTo] as AbstractControl;
      if (c) {
        c.updateValueAndValidity();
      }
      return null;
    }
    return !!parent &&
      !!parent.value &&
      control.value === (parent?.controls as any)[matchTo].value
      ? null
      : { matching: true };
  };
}

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
})
export class SignUpComponent implements OnInit, OnDestroy {
  @ViewChild('inputCity') inputCity: IonInput;
  form: FormGroup;
  countries$ = new BehaviorSubject<ICountry[]>([]);
  type = 'password';
  iconName = 'eye';
  typeConfirm = 'password';
  iconNameConfirm = 'eye';
  isValidLink = true;
  private loadSpinner = new BehaviorSubject(false);
  loadSpinner$ = this.loadSpinner.asObservable();
  private destroy$ = new Subject<void>();
  RefferdObj: IAffiliates | undefined;
  affiliateArr: any;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private toastController: ToastController,
    private formService: FormService,
    private route: ActivatedRoute,
    private mapService: MapService,
    private fireStore: AngularFirestore
  ) {}

  // Get the affiliate code from the URL if it exists
  affiliateCode = this.route.snapshot.paramMap.get('id');

  ngOnInit() {
    this.formService.location$.next({ googleMapLink: null });
    this.fireStore
      .collection('affiliates')
      .valueChanges()
      .subscribe(data => {
        this.affiliateArr = data;
      });

    this.form = this.fb.group(
      {
        username: ['', Validators.required],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(6),
            matchValidator('confirmPassword', true),
          ],
        ],
        repeatPassword: ['', [Validators.required, matchValidator('password')]],
        conditions: [false, Validators.requiredTrue],
        privacyPolicy: [false, Validators.requiredTrue],
        isMailingEnabled: [false],
        email: ['', [Validators.required, Validators.email]],
        address: this.fb.group({
          country: [''],
          googleMapLink: ['', [Validators.required]],
        }),
        affiliateCode: [this.affiliateCode || ''], // Add affiliateCode to the form
      },
      {
        validators: [matchValidator],
      }
    );
    this.countries$.next(countries);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async addMapLink(e) {
    e.valueChanges.subscribe(async () => {
      this.isValidLink = true;
      const location = e.get('googleMapLink')?.value;
      const { country } = await this.mapService.getAddress(location);
      if (!country) {
        this.isValidLink = false;
      }

      this.form.get('address.googleMapLink')?.patchValue(location);
      this.form.get('address').patchValue({ country });
    });
  }

  async signUp() {
    this.loadSpinner.next(true);
    const user: IUser = this.form.value;
    var isAff = this.affiliateArr?.find(
      (i: IAffiliates) => i?.affilaiteCode === user?.affiliateCode
    );

    if (!isAff && user.affiliateCode) {
      this.createToaster('Oops! This affiliate code doesn’t exist.');
      this.loadSpinner.next(false);
    } else {
      this.RefferdObj = isAff;
      try {
        await this.authService.signUp(user);
        // Successful sign-up, show a success popup
        if (user.affiliateCode) {
          this.showSuccessPopup();
        } else {
          this.router.navigate(['/tutorial'], { replaceUrl: true });
        }
      } catch (error) {
        switch (error.code) {
          case 'auth/email-already-in-use':
            this.createToaster('User email already exists');
            break;
          case 'auth/invalid-email':
            this.createToaster('Email address is not valid');
            break;
          case 'auth/weak-password':
            this.createToaster('Password is not strong enough');
            break;
          case 'auth/operation-not-allowed':
            this.createToaster('Operation not allowed');
            break;
          case 'auth/username-already-exist':
            this.createToaster('Username already exist');
            break;
          case 'auth/invalid-address':
            this.createToaster('Address is not valid');
            break;
          default:
            this.createToaster('Firebase error');
        }
      } finally {
        this.loadSpinner.next(false);
      }
    }
  }

  changeType() {
    this.type = this.type === 'password' ? 'text' : 'password';
    this.iconName = this.iconName === 'eye' ? 'eye-off' : 'eye';
  }

  changeTypeConfirm() {
    this.typeConfirm = this.typeConfirm === 'password' ? 'text' : 'password';
    this.iconNameConfirm = this.iconNameConfirm === 'eye' ? 'eye-off' : 'eye';
  }

  private async createToaster(message: string) {
    const t = await this.toastController.create({
      message,
      color: 'danger',
      duration: 1500,
      position: 'top',
    });
    t.present();
  }

  showSuccessPopup(): void {
    const popup = document.getElementById('popup');
    if (popup) {
      popup.style.display = 'flex';
    }
  }

  collectReward = async () => {
    this.authService.collectReward();
  };

  closePopup(): void {
    this.authService.closePopup();
  }
}
